import React from "react";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { ChevronDownIcon } from "../Dashboard/icons";
import { SunIcon, MoonIcon } from "./icons";
import {
  CUSTOM_POSITION,
  isCustomPosition,
  DEFAULT_CUSTOM_OFFSET,
} from "./widgetPlacement";

const initialStates = ["Minimized", "Expanded"];
const autoOpenOptions = [
  "Immediately",
  "5 seconds",
  "10 seconds",
  "30 seconds",
  "Never",
];

export default function WidgetLayoutCard({ settings, onChange }) {
  const custom = isCustomPosition(settings.widgetPosition);
  const offsetX = settings.widgetOffsetX ?? DEFAULT_CUSTOM_OFFSET.x;
  const offsetY = settings.widgetOffsetY ?? DEFAULT_CUSTOM_OFFSET.y;

  return (
    <section className="dash-card cz-card">
      <h2 className="cz-card-title">4. Widget layout</h2>

      <div className="cz-field-row">
        <div className="cz-field">
          <span className="cz-field-label">Widget position</span>
          <div className="cz-radio-group">
            <button
              type="button"
              className={settings.widgetPosition === "right" ? "is-active" : ""}
              onClick={() => onChange("widgetPosition", "right")}
            >
              <span className="cz-radio-dot" />
              Bottom right
            </button>
            <button
              type="button"
              className={settings.widgetPosition === "left" ? "is-active" : ""}
              onClick={() => onChange("widgetPosition", "left")}
            >
              <span className="cz-radio-dot" />
              Bottom left
            </button>
            <button
              type="button"
              className={custom ? "is-active" : ""}
              onClick={() => onChange("widgetPosition", CUSTOM_POSITION)}
            >
              <span className="cz-radio-dot" />
              Custom
            </button>
          </div>
        </div>

        <div className="cz-field">
          <span className="cz-field-label">Border radius</span>
          <span className="cz-slider-row">
            <input
              type="range"
              min="0"
              max="28"
              value={settings.borderRadius}
              onChange={(e) => onChange("borderRadius", Number(e.target.value))}
            />
            <span className="cz-slider-value">{settings.borderRadius}px</span>
          </span>
        </div>
      </div>

      {/* Only with Custom picked: the free placement. Dragging the widget in
          the preview writes the same two numbers, so the sliders are both the
          precise way in and the keyboard-reachable one. */}
      {custom && (
        <div className="cz-field-row cz-custom-position">
          <div className="cz-field">
            <span className="cz-field-label">Distance from left</span>
            <span className="cz-slider-row">
              <input
                type="range"
                min="0"
                max="100"
                value={offsetX}
                onChange={(e) =>
                  onChange("widgetOffsetX", Number(e.target.value))
                }
              />
              <span className="cz-slider-value">{offsetX}%</span>
            </span>
          </div>

          <div className="cz-field">
            <span className="cz-field-label">Distance from top</span>
            <span className="cz-slider-row">
              <input
                type="range"
                min="0"
                max="100"
                value={offsetY}
                onChange={(e) =>
                  onChange("widgetOffsetY", Number(e.target.value))
                }
              />
              <span className="cz-slider-value">{offsetY}%</span>
            </span>
          </div>

          <p className="cz-field-hint">
            Drag the widget in the preview to place it, or use the sliders.
            Both are a share of the screen, so the spot holds up on any
            display size.
          </p>
        </div>
      )}

      <div className="cz-field-row">
        <div className="cz-field">
          <span className="cz-field-label">Bubble style</span>
          <div className="cz-radio-group">
            <button
              type="button"
              className={settings.bubbleStyle === "rounded" ? "is-active" : ""}
              onClick={() => onChange("bubbleStyle", "rounded")}
            >
              <span className="cz-radio-dot" />
              Rounded
            </button>
            <button
              type="button"
              className={settings.bubbleStyle === "squircle" ? "is-active" : ""}
              onClick={() => onChange("bubbleStyle", "squircle")}
            >
              <span className="cz-radio-dot" />
              Squircle
            </button>
          </div>
        </div>

        <div className="cz-field">
          <span className="cz-field-label">Theme</span>
          <div className="cz-segmented cz-segmented--icon">
            <button
              type="button"
              className={settings.theme === "light" ? "is-active" : ""}
              onClick={() => onChange("theme", "light")}
            >
              <SunIcon />
              Light
            </button>
            <button
              type="button"
              className={settings.theme === "dark" ? "is-active" : ""}
              onClick={() => onChange("theme", "dark")}
            >
              <MoonIcon />
              Dark
            </button>
          </div>
        </div>

        <div className="cz-field">
          <span className="cz-field-label">Spacing</span>
          <div className="cz-segmented">
            <button
              type="button"
              className={settings.spacing === "compact" ? "is-active" : ""}
              onClick={() => onChange("spacing", "compact")}
            >
              Compact
            </button>
            <button
              type="button"
              className={settings.spacing === "comfortable" ? "is-active" : ""}
              onClick={() => onChange("spacing", "comfortable")}
            >
              Comfortable
            </button>
          </div>
        </div>
      </div>

      {/* Behaviour, merged in from its own card. Widget position isn't
          repeated here — it's already the first control above. */}
      <div className="cz-field-row">
        <div className="cz-field">
          <span className="cz-field-label">Initial state</span>
          <span className="cz-select-wrap">
            <select
              value={settings.initialState}
              onChange={(e) => onChange("initialState", e.target.value)}
            >
              {initialStates.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDownIcon />
          </span>
        </div>

        <div className="cz-field">
          <span className="cz-field-label">Auto open after</span>
          <span className="cz-select-wrap">
            <select
              value={settings.autoOpenAfter}
              onChange={(e) => onChange("autoOpenAfter", e.target.value)}
            >
              {autoOpenOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDownIcon />
          </span>
        </div>
      </div>

      <div className="cz-toggle-list">
        <label className="cz-toggle-row">
          <ToggleSwitch
            checked={settings.showWelcomeMessage}
            onChange={(v) => onChange("showWelcomeMessage", v)}
            label="Show welcome message"
          />
          Show welcome message
        </label>
        <label className="cz-toggle-row">
          <ToggleSwitch
            checked={settings.showAvatar}
            onChange={(v) => onChange("showAvatar", v)}
            label="Show avatar"
          />
          Show avatar
        </label>
        {/* <label className="cz-toggle-row">
          <ToggleSwitch
            checked={settings.allowClose}
            onChange={(v) => onChange("allowClose", v)}
            label="Allow user to close widget"  
          />
          Allow user to close widget
        </label> */}
      </div>
    </section>
  );
}
